/* eslint-disable react/jsx-props-no-spreading */
import type { FocusableElement } from '@react-types/shared';
import type * as React from 'react';

import { useCallback, useEffect } from 'react';

import type { AnalyticsListOptions } from '../../../../analytics/analyticsHandler';
import type { ApiProduct } from '../../../../models/ecommerce/product/ApiProduct';
import type { ItemTeaserProps } from '../../models/props/itemTeaserProps';

import { useBuyableItemGATracking } from '../../hooks/tracking/useBuyableItemGATracking';
import { buyableProductEventsTracking } from '../../hooks/useBuyableProductEventsTracking';
import { useBuyableProductState } from '../../hooks/useBuyableProductState';
import { useCurrentPriceType } from '../../hooks/useCurrentPriceType';
import { ProductSearchListingItem } from '../ProductSearchListingItem';
import { mapToProductSearchListingItemData } from '../ProductSearchListingItem/ProductSearchListingItem.utils';

interface BuyableSearchProductProps extends Omit<ItemTeaserProps, 'variantId' | 'product'> {
    product: ApiProduct;
    silent: boolean;
    analyticsOptions: AnalyticsListOptions;
    onCartItemError?: (err: string) => void;
    onAddToCartClick?: React.MouseEventHandler<FocusableElement>;
    onRemoveFromCartClick?: React.MouseEventHandler<FocusableElement>;
}

const BuyableSearchProduct = (props: BuyableSearchProductProps) => {
    const {
        currentVariant,
        quantity,
        handleVariantChange,
        handleQuantityUpdate,
        addToCartDispatcher,
        updateQuantityDispatcher,
        removeFromCartDispatcher,
    } = useBuyableProductState(props.product, {
        isSilent: props.silent,
        analyticsOptions: props.analyticsOptions,
    });

    const { currentPriceType } = useCurrentPriceType();

    const onQuantityUpdate = useCallback(
        (newQuantity: number) => {
            handleQuantityUpdate(
                newQuantity,
                () =>
                    buyableProductEventsTracking.onAddToCartTrack({
                        identifier: props.product.identifier,
                        priceData: props.product.priceData,
                        quantity: newQuantity,
                        previousQuantity: 0,
                        promotionPriceData: props.product.promotionPriceData,
                    }),
                () =>
                    buyableProductEventsTracking.onQuantityUpdateTrack({
                        identifier: props.product.identifier,
                        priceData: props.product.priceData,
                        quantity: newQuantity,
                        previousQuantity: quantity,
                        promotionPriceData: props.product.promotionPriceData,
                    }),
                () =>
                    buyableProductEventsTracking.onRemoveFromCartTrack({
                        identifier: props.product.identifier,
                        priceData: props.product.priceData,
                        quantity: newQuantity,
                        previousQuantity: quantity,
                        promotionPriceData: props.product.promotionPriceData,
                    }),
            );
        },
        [
            handleQuantityUpdate,
            props.product.identifier,
            props.product.priceData,
            props.product.promotionPriceData,
            quantity,
        ],
    );

    const { onCartItemError } = props;

    useEffect(() => {
        if (
            onCartItemError &&
            (addToCartDispatcher.error ||
                updateQuantityDispatcher.error ||
                removeFromCartDispatcher.error)
        ) {
            onCartItemError(
                addToCartDispatcher.error?.friendlyMessage ||
                    updateQuantityDispatcher.error?.friendlyMessage ||
                    removeFromCartDispatcher.error?.friendlyMessage,
            );
        }
    }, [
        addToCartDispatcher.error,
        updateQuantityDispatcher.error,
        removeFromCartDispatcher.error,
        onCartItemError,
    ]);

    const { ref, handleGaProductClick } = useBuyableItemGATracking(
        props.product,
        currentVariant?.id,
        props.analyticsOptions,
    );

    const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        handleGaProductClick();
        props.handleLinkClick?.(e);
    };

    const productData = mapToProductSearchListingItemData(
        props.product,
        currentVariant || undefined,
    );

    return (
        <ProductSearchListingItem
            ref={ref}
            product={productData}
            isALink={props.isALink}
            isAsyncLink={props.isAsyncLink}
            currentVariant={currentVariant || undefined}
            handleVariantChange={handleVariantChange}
            handleLinkClick={handleLinkClick}
            quantity={quantity}
            onQuantityUpdate={onQuantityUpdate}
            priceType={currentPriceType}
            onBuyButtonClick={props.onAddToCartClick}
            onAddToCartClick={props.onAddToCartClick}
            onRemoveFromCartClick={props.onRemoveFromCartClick}
        />
    );
};

export default BuyableSearchProduct;
